/**
 * Step 2, addressed by URL: /add-organization-to-events/:organizationId. The
 * organization is loaded from the id, so the page survives a reload.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Loader, Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { getOrganization } from '../api';
import { i18nKeys } from '../constants';
import { searchPathFrom } from '../helpers';
import { Organization } from '../types';
import EventSelection from './EventSelection';

interface OrganizationStepProps {
  /** Null when the route id is not a valid organisation id. */
  organizationId: number | null;
}

const OrganizationStep: React.FC<OrganizationStepProps> = ({
  organizationId
}) => {
  const navigate = useNavigate();
  const searchPath = searchPathFrom(useLocation().state);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // Ignore a response for an id the URL has already moved past, so a slow
    // earlier request cannot replace the organisation being added.
    let isCurrent = true;
    setOrganization(null);
    setLoadError(null);
    if (organizationId === null) {
      setLoadError(I18n.t(i18nKeys.ORGANIZATION.NOT_FOUND));
      return;
    }
    getOrganization(organizationId)
      .then((loaded) => {
        if (isCurrent) {
          setOrganization(loaded);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setLoadError(I18n.t(i18nKeys.ORGANIZATION.LOAD_FAILED));
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [organizationId]);

  if (loadError) {
    return (
      <Message negative>
        <p>{loadError}</p>
        <Button as={Link} to={searchPath} size="small">
          <Translate value={i18nKeys.ORGANIZATION.BACK_TO_SEARCH} />
        </Button>
      </Message>
    );
  }

  if (!organization) {
    return <Loader active inline="centered" />;
  }

  // Step 1 never links to such an organization, but a typed or stale URL can.
  if (!organization.hasPaymentPlatform) {
    return (
      <Message negative>
        <p>
          {I18n.t(i18nKeys.ORGANIZATION.NO_PAYMENT_PLATFORM, {
            name: organization.name
          })}
        </p>
        <Button as={Link} to={searchPath} size="small">
          <Translate value={i18nKeys.ORGANIZATION.BACK_TO_SEARCH} />
        </Button>
      </Message>
    );
  }

  return (
    <EventSelection
      organization={organization}
      searchPath={searchPath}
      onCancel={() => navigate(searchPath)}
    />
  );
};

export default OrganizationStep;
